(() => {
  if (window.__blendStagedHeaderInitialized) return;
  window.__blendStagedHeaderInitialized = true;
  const SELECTOR = '[data-blend-staged-header]';
  const SCROLL_ENTER_THRESHOLD = 32;
  const SCROLL_EXIT_THRESHOLD = 8;
  const MENU_SAFE_ZONE_PADDING = 24;
  const mountedHeaders = new Map();

  const mountHeader = (header) => {
    if (mountedHeaders.has(header)) return;

    header.dataset.blendHeaderMounted = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    let frameId = null;
    let safeZoneObserver;
    let isScrolled = window.scrollY > SCROLL_ENTER_THRESHOLD;
    const drawerContainer = header.querySelector('header-drawer > details');
    const drawerSummary = drawerContainer?.querySelector(':scope > summary');
    const drawerElement = drawerContainer?.closest('header-drawer');
    const desktopNavigation = header.querySelector('.header__inline-menu');
    const desktopMenus = [...header.querySelectorAll('.header__inline-menu .mega-menu')];
    const desktopSearch = header.querySelector('.blend-desktop-search');
    const desktopSearchInput = desktopSearch?.querySelector('input[type="search"]');

    const closeDesktopSearch = (clearSearchTerm = false) => {
      if (typeof desktopSearch?.close === 'function') desktopSearch.close(clearSearchTerm);
      if (clearSearchTerm && desktopSearchInput) {
        desktopSearchInput.value = '';
        if (typeof desktopSearch.toggleResetButton === 'function') desktopSearch.toggleResetButton();
      }
      desktopSearch?.removeAttribute('open');
      desktopSearchInput?.setAttribute('aria-expanded', 'false');
    };

    const updateState = () => {
      frameId = null;
      if (signal.aborted || !header.isConnected) return;
      const scrollPosition = Math.max(0, window.scrollY);

      if (!isScrolled && scrollPosition > SCROLL_ENTER_THRESHOLD) {
        isScrolled = true;
      } else if (isScrolled && scrollPosition < SCROLL_EXIT_THRESHOLD) {
        isScrolled = false;
      }

      const nextState = String(isScrolled);
      if (header.dataset.scrolled !== nextState) {
        header.dataset.scrolled = nextState;
      }
    };

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateState);
    };

    updateState();
    window.addEventListener('scroll', onScroll, { passive: true, signal });

    if (desktopNavigation && desktopMenus.length > 0) {
      const syncMenuSafeZones = () => {
        if (signal.aborted || !header.isConnected) return;
        desktopMenus.forEach((menu) => {
          const trigger = menu.querySelector(':scope > .header__menu-item');
          const panel = menu.querySelector(':scope > .mega-menu__content');
          if (!trigger || !panel) return;

          const safeTop = trigger.offsetTop + trigger.offsetHeight / 2;
          const safeHeight = Math.max(12, panel.offsetTop + 2 - safeTop);

          menu.style.setProperty('--blend-menu-trigger-left', `${trigger.offsetLeft}px`);
          menu.style.setProperty('--blend-menu-trigger-width', `${trigger.offsetWidth}px`);
          menu.style.setProperty('--blend-menu-safe-top', `${safeTop}px`);
          menu.style.setProperty(
            '--blend-menu-safe-left',
            `${panel.offsetLeft - MENU_SAFE_ZONE_PADDING}px`,
          );
          menu.style.setProperty(
            '--blend-menu-safe-width',
            `${panel.offsetWidth + MENU_SAFE_ZONE_PADDING * 2}px`,
          );
          menu.style.setProperty('--blend-menu-safe-height', `${safeHeight}px`);
        });
      };

      syncMenuSafeZones();
      document.fonts?.ready.then(syncMenuSafeZones);

      if ('ResizeObserver' in window) {
        safeZoneObserver = new ResizeObserver(syncMenuSafeZones);
        safeZoneObserver.observe(desktopNavigation);
      }

      desktopMenus.forEach((menu) => {
        menu.addEventListener('pointerenter', () => closeDesktopSearch(true), { signal });
        menu.addEventListener('focusin', () => closeDesktopSearch(true), { signal });
      });
    }

    if (drawerContainer && drawerSummary) {
      const removeOutsidePointerListener = () => {
        document.removeEventListener('pointerdown', onOutsidePointerDown);
      };

      const closeDrawer = (event) => {
        removeOutsidePointerListener();

        if (typeof drawerElement?.closeMenuDrawer === 'function') {
          drawerElement.closeMenuDrawer(event, drawerSummary);
        } else {
          drawerSummary.click();
        }
      };

      function onOutsidePointerDown(event) {
        if (!drawerContainer.open || event.button !== 0 || drawerContainer.contains(event.target)) {
          return;
        }

        closeDrawer(event);
      }

      drawerContainer.addEventListener('toggle', () => {
        drawerSummary.setAttribute('aria-expanded', String(drawerContainer.open));

        if (drawerContainer.open) {
          document.addEventListener('pointerdown', onOutsidePointerDown, { signal });
        } else {
          removeOutsidePointerListener();
        }
      }, { signal });

      drawerContainer.addEventListener('click', (event) => {
        if (event.target !== drawerContainer || !drawerContainer.open) return;
        drawerSummary.click();
      }, { signal });
    }

    mountedHeaders.set(header, () => {
      controller.abort();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = null;
      safeZoneObserver?.disconnect();
      delete header.dataset.blendHeaderMounted;
      mountedHeaders.delete(header);
    });
  };

  const mountAllHeaders = (root = document) => {
    if (root.matches?.(SELECTOR)) mountHeader(root);
    root.querySelectorAll(SELECTOR).forEach(mountHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAllHeaders(), { once: true });
  } else {
    mountAllHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => mountAllHeaders(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    mountedHeaders.forEach((cleanup, header) => {
      if (event.target === header || event.target.contains(header)) cleanup();
    });
  });
})();
